// Sistema de Quiz sobre Homeopatia
// Gerencia perguntas, respostas, pontuação e estado do quiz para cada membro

import QuizService from '../services/quizService';
import type { QuizQuestion as QuizQuestionDB } from '../services/quizService';
import { logger } from './logger';

// Interface para compatibilidade com código legado
export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  difficulty: 'fácil' | 'médio' | 'difícil';
  category: 'princípios básicos' | 'medicamentos' | 'potências' | 'história';
  explanation?: string;
}

// Função para converter questão do banco para formato legado
function convertQuestionToLegacy(dbQuestion: QuizQuestionDB): QuizQuestion {
  const difficultyMap: Record<string, 'fácil' | 'médio' | 'difícil'> = {
    easy: 'fácil',
    medium: 'médio',
    hard: 'difícil'
  };
  
  const categoryMap: Record<string, 'princípios básicos' | 'medicamentos' | 'potências' | 'história'> = {
    basic_principles: 'princípios básicos',
    remedies: 'medicamentos',
    potencies: 'potências',
    history: 'história'
  };
  
  return {
    id: dbQuestion.id,
    question: dbQuestion.question,
    options: dbQuestion.options,
    correctAnswer: dbQuestion.correctAnswer,
    difficulty: difficultyMap[dbQuestion.difficulty] || 'fácil',
    category: categoryMap[dbQuestion.category] || 'princípios básicos',
    explanation: dbQuestion.explanation
  };
}

export interface QuizAttempt {
  questionId: string;
  selectedAnswer: number;
  isCorrect: boolean;
  timestamp: number;
  pointsEarned: number;
}

export interface MemberQuizState {
  memberId: string;
  dailyQuestions: string[]; // IDs das perguntas do dia
  completedAttempts: QuizAttempt[];
  totalPointsToday: number;
  lastQuizDate: string; // YYYY-MM-DD
  streakDays: number;
  totalQuizzesCompleted: number;
  averageScore: number;
}

export interface QuizSummary {
  date: string;
  questionsAnswered: number;
  correctAnswers: number;
  totalPoints: number;
  accuracy: number; // percentual de acertos
  categories: {
    [key: string]: {
      answered: number;
      correct: number;
    };
  };
}

export class QuizSystem {
  private static instance: QuizSystem;
  private quizService: QuizService;
  private static readonly STORAGE_KEY = 'homeopathy_quiz_data';
  private static readonly QUESTIONS_PER_DAY = 3;
  private static readonly POINTS_PER_CORRECT_ANSWER = 10;
  private static readonly COMPLETION_BONUS_POINTS = 50;
  private static readonly TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

  private constructor() {
    this.quizService = QuizService.getInstance();
  }

  static getInstance(): QuizSystem {
    if (!QuizSystem.instance) {
      QuizSystem.instance = new QuizSystem();
    }
    return QuizSystem.instance;
  }

  // Carrega estado do quiz para um membro específico
  getMemberQuizState(memberId: string): MemberQuizState {
    const allData = this.getAllQuizData();
    
    let memberState = allData[memberId];
    
    if (!memberState) {
      memberState = {
        memberId,
        dailyQuestions: [],
        completedAttempts: [],
        totalPointsToday: 0,
        lastQuizDate: '',
        streakDays: 0,
        totalQuizzesCompleted: 0,
        averageScore: 0
      };
    }

    // Nota: A renovação de perguntas diárias agora é assíncrona
    // e deve ser chamada explicitamente quando necessário

    return memberState;
  }

  // Verifica se precisa renovar perguntas e renova se necessário
  async checkAndRenewQuestions(memberId: string): Promise<void> {
    const memberState = this.getMemberQuizState(memberId);
    const today = this.getTodayString();
    
    if (memberState.lastQuizDate !== today || memberState.dailyQuestions.length === 0) {
      await this.renewDailyQuestions(memberState);
    }
  }

  // Salva estado do quiz para um membro
  saveMemberQuizState(memberState: MemberQuizState): void {
    const allData = this.getAllQuizData();
    allData[memberState.memberId] = memberState;
    localStorage.setItem(QuizSystem.STORAGE_KEY, JSON.stringify(allData));
  }

  // Renova as perguntas diárias para um membro
  async renewDailyQuestions(memberState: MemberQuizState): Promise<MemberQuizState> {
    const today = this.getTodayString();
    
    try {
      // Busca perguntas do banco de dados
      const dbQuestions = await this.quizService.getDailyQuestions(memberState.memberId);
      
      // Verifica se há perguntas disponíveis
      if (!dbQuestions || dbQuestions.length === 0) {
        console.warn('Nenhuma pergunta disponível no banco de dados');
        return memberState;
      }
      
      const updatedState = {
        ...memberState,
        dailyQuestions: dbQuestions.map(q => q.id),
        completedAttempts: [],
        totalPointsToday: 0,
        lastQuizDate: today
      };

      // Salva o estado atualizado
      this.saveMemberQuizState(updatedState);
      
      return updatedState;
    } catch (error) {
      console.error('Erro ao renovar perguntas diárias:', error);
      return memberState;
    }
  }


  // Submete uma resposta do quiz
  async submitQuizAnswer(
    memberId: string, 
    questionId: string, 
    selectedAnswer: number,
    userId: string
  ): Promise<{ isCorrect: boolean; pointsEarned: number; explanation?: string }> {
    const memberState = this.getMemberQuizState(memberId);
    
    // VALIDAÇÃO: Impedir responder mais de 3 perguntas
    if (memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY) {
      throw new Error('Limite diário de perguntas atingido. Você já respondeu 3 perguntas hoje.');
    }
    
    try {
      // Busca pergunta do banco
      const dbQuestion = await this.quizService.getQuestionById(questionId);
      
      if (!dbQuestion) {
        throw new Error('Pergunta não encontrada');
      }
      
      const question = convertQuestionToLegacy(dbQuestion);

      const isCorrect = selectedAnswer === question.correctAnswer;
      let pointsEarned = 0;

      // Cria tentativa
      const attempt: QuizAttempt = {
        questionId,
        selectedAnswer,
        isCorrect,
        timestamp: Date.now(),
        pointsEarned: isCorrect ? QuizSystem.POINTS_PER_CORRECT_ANSWER : 0
      };

      // Registra tentativa no banco
      await this.quizService.recordAttempt({
        userId,
        memberId,
        questionId,
        selectedAnswer,
        isCorrect,
        pointsEarned: isCorrect ? QuizSystem.POINTS_PER_CORRECT_ANSWER : 0
      });

      // Atualiza estado do membro
      memberState.completedAttempts.push(attempt);
      memberState.totalQuizzesCompleted++;
      
      // Calcula pontos e registra no sistema de pontuação
      if (isCorrect) {
        pointsEarned = QuizSystem.POINTS_PER_CORRECT_ANSWER;
      }

      memberState.totalPointsToday += pointsEarned;
      
      // Verificar se completou o quiz diário (3 perguntas)
      // Usar >= para garantir que o bônus seja dado mesmo se houver algum erro
      if (memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY) {
        const bonusPoints = QuizSystem.COMPLETION_BONUS_POINTS;
        memberState.totalPointsToday += bonusPoints;
        pointsEarned += bonusPoints;

        const correctAnswers = memberState.completedAttempts.filter(a => a.isCorrect).length;
        const metadata = {
          questionIds: memberState.completedAttempts.map(a => a.questionId),
          completedAttempts: memberState.completedAttempts.length
        };

        await this.quizService.recordDailyCompletion({
          userId,
          memberId,
          questionsAnswered: memberState.completedAttempts.length,
          correctAnswers,
          totalQuestions: Math.max(memberState.dailyQuestions.length, QuizSystem.QUESTIONS_PER_DAY),
          totalPoints: memberState.totalPointsToday,
          completionDate: new Date().toISOString(),
          metadata
        });
      }
      
      // Recalcula média de acertos
      const totalCorrect = memberState.completedAttempts.filter(a => a.isCorrect).length;
      memberState.averageScore = (totalCorrect / memberState.completedAttempts.length) * 100;

      this.saveMemberQuizState(memberState);

      return {
        isCorrect,
        pointsEarned,
        explanation: question.explanation
      };
    } catch (error) {
      console.error('Erro ao submeter resposta:', error);
      throw error;
    }
  }

  // Verifica se o membro tem quiz disponível
  hasAvailableQuiz(memberId: string): boolean {
    const memberState = this.getMemberQuizState(memberId);
    return memberState.completedAttempts.length < memberState.dailyQuestions.length;
  }

  // Obtém próxima pergunta disponível
  async getNextQuestion(memberId: string): Promise<QuizQuestion | null> {
    const memberState = this.getMemberQuizState(memberId);
    
    // VALIDAÇÃO: Verificar limite de 3 perguntas PRIMEIRO
    if (memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY) {
      console.log('🚫 [QuizSystem] Limite diário atingido:', memberState.completedAttempts.length, 'perguntas');
      return null; // Já completou as 3 perguntas do dia
    }
    
    // Verifica se há perguntas diárias disponíveis
    if (!memberState.dailyQuestions || memberState.dailyQuestions.length === 0) {
      console.warn('Nenhuma pergunta diária disponível para o membro:', memberId);
      return null;
    }
    
    const completedQuestionIds = memberState.completedAttempts.map(a => a.questionId);
    const nextQuestionId = memberState.dailyQuestions.find(id => !completedQuestionIds.includes(id));
    
    if (!nextQuestionId) {
      return null; // Todas as perguntas foram respondidas
    }

    try {
      const dbQuestion = await this.quizService.getQuestionById(nextQuestionId);
      
      if (!dbQuestion) {
        console.error('Pergunta não encontrada com ID:', nextQuestionId);
        return null;
      }

      return convertQuestionToLegacy(dbQuestion);
    } catch (error) {
      console.error('Erro ao buscar próxima pergunta:', error);
      return null;
    }
  }

  // Gera resumo diário do quiz
  async getDailySummary(memberId: string): Promise<QuizSummary> {
    const memberState = this.getMemberQuizState(memberId);
    
    const questionsAnswered = memberState.completedAttempts.length;
    const correctAnswers = memberState.completedAttempts.filter(a => a.isCorrect).length;
    const accuracy = questionsAnswered > 0 ? (correctAnswers / questionsAnswered) * 100 : 0;

    // Agrupa por categoria
    const categories: { [key: string]: { answered: number; correct: number } } = {};
    
    try {
      for (const attempt of memberState.completedAttempts) {
        const dbQuestion = await this.quizService.getQuestionById(attempt.questionId);
        if (dbQuestion) {
          const question = convertQuestionToLegacy(dbQuestion);
          if (!categories[question.category]) {
            categories[question.category] = { answered: 0, correct: 0 };
          }
          categories[question.category].answered++;
          if (attempt.isCorrect) {
            categories[question.category].correct++;
          }
        }
      }
    } catch (error) {
      console.error('Erro ao gerar resumo:', error);
    }

    return {
      date: memberState.lastQuizDate,
      questionsAnswered,
      correctAnswers,
      totalPoints: memberState.totalPointsToday,
      accuracy,
      categories
    };
  }

  // Verifica se o membro completou todos os quiz do dia
  isQuizCompletedToday(memberId: string): boolean {
    const memberState = this.getMemberQuizState(memberId);
    return memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY;
  }

  // Obtém progresso do quiz (quantas perguntas respondidas de quantas disponíveis)
  getQuizProgress(memberId: string): { completed: number; total: number } {
    const memberState = this.getMemberQuizState(memberId);
    return {
      completed: memberState.completedAttempts.length,
      total: QuizSystem.QUESTIONS_PER_DAY
    };
  }

  // Métodos utilitários
  private getAllQuizData(): { [memberId: string]: MemberQuizState } {
    try {
      const data = localStorage.getItem(QuizSystem.STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Erro ao carregar dados do quiz:', error);
      return {};
    }
  }

  private getTodayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Obtém estatísticas globais do quiz
  async getGlobalStats(): Promise<{
    totalQuestions: number;
    totalMembers: number;
    averageAccuracy: number;
    questionsPerCategory: { [category: string]: number };
    dailyCompletions: number;
  }> {
    const allData = this.getAllQuizData();
    const today = this.getTodayString();
    
    const totalMembers = Object.keys(allData).length;
    let totalAccuracy = 0;
    let membersWithData = 0;
    let dailyCompletions = 0;
    
    const questionsPerCategory: { [category: string]: number } = {};
    
    try {
      for (const memberState of Object.values(allData)) {
        if (memberState.completedAttempts.length > 0) {
          membersWithData++;
          const correctAnswers = memberState.completedAttempts.filter(a => a.isCorrect).length;
          const accuracy = (correctAnswers / memberState.completedAttempts.length) * 100;
          totalAccuracy += accuracy;
          
          if (memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY && memberState.lastQuizDate === today) {
            dailyCompletions++;
          }
          
          // Conta perguntas por categoria
          for (const questionId of memberState.dailyQuestions) {
            const dbQuestion = await this.quizService.getQuestionById(questionId);
            if (dbQuestion) {
              const question = convertQuestionToLegacy(dbQuestion);
              questionsPerCategory[question.category] = (questionsPerCategory[question.category] || 0) + 1;
            }
          }
        }
      }
      
      const globalStats = await this.quizService.getGlobalStats();
      
      return {
        totalQuestions: globalStats.totalQuestions,
        totalMembers,
        averageAccuracy: membersWithData > 0 ? totalAccuracy / membersWithData : 0,
        questionsPerCategory,
        dailyCompletions
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas globais:', error);
      return {
        totalQuestions: 0,
        totalMembers,
        averageAccuracy: 0,
        questionsPerCategory: {},
        dailyCompletions: 0
      };
    }
  }

  // Limpa todos os dados do quiz (para testes)
  clearAllQuizData(): void {
    localStorage.removeItem(QuizSystem.STORAGE_KEY);
  }

  // Verifica se o membro completou o quiz nas últimas 24h (banco de dados)
  async hasCompletedQuizInLast24Hours(memberId: string): Promise<boolean> {
    try {
      const { supabase } = await import('../lib/supabase');
      const twentyFourHoursAgo = new Date(Date.now() - QuizSystem.TWENTY_FOUR_HOURS).toISOString();
      
      // Buscar tentativas nas últimas 24h
      const { data, error } = await supabase
        .from('quiz_answer_attempts' as any)
        .select('id, created_at')
        .eq('member_id', memberId)
        .gte('created_at', twentyFourHoursAgo)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ [QuizSystem] Erro ao verificar quiz no banco:', error);
        return false;
      }
      
      // Verificar se tem pelo menos 3 tentativas (quiz completo)
      const hasCompleted = data && data.length >= QuizSystem.QUESTIONS_PER_DAY;
      logger.sensitive('Quiz completado nas últimas 24h?', { memberId, completed: hasCompleted, attempts: data?.length ?? 0 });
      return hasCompleted;
    } catch (error) {
      console.error('❌ [QuizSystem] Erro ao verificar quiz:', error);
      return false;
    }
  }

  // Obtém status do quiz para o membro (verifica banco de dados)
  async getQuizStatus(memberId: string): Promise<{
    hasCompletedToday: boolean;
    canTakeQuiz: boolean;
    questionsAnswered: number;
    totalQuestions: number;
  }> {
    try {
      // Verificar no banco de dados se completou nas últimas 24h
      const hasCompletedIn24h = await this.hasCompletedQuizInLast24Hours(memberId);
      
      // Se completou nas últimas 24h no banco, retorna que não pode fazer quiz
      if (hasCompletedIn24h) {
        return {
          hasCompletedToday: true,
          canTakeQuiz: false,
          questionsAnswered: QuizSystem.QUESTIONS_PER_DAY,
          totalQuestions: QuizSystem.QUESTIONS_PER_DAY
        };
      }
      
      // Se NÃO completou nas últimas 24h no banco, limpa o localStorage
      // para garantir que o estado local está sincronizado
      const memberState = this.getMemberQuizState(memberId);
      
      // Verifica se o localStorage tem dados de quiz completado
      if (memberState.completedAttempts.length >= QuizSystem.QUESTIONS_PER_DAY) {
        // Se o banco diz que não completou mas o localStorage diz que sim,
        // significa que já se passaram 24h - limpa o localStorage
        console.log('🔄 [QuizSystem] Limpando estado local desatualizado para membro:', memberId);
        await this.renewDailyQuestions(memberState);
        
        return {
          hasCompletedToday: false,
          canTakeQuiz: true,
          questionsAnswered: 0,
          totalQuestions: QuizSystem.QUESTIONS_PER_DAY
        };
      }
      
      // Retorna o progresso atual do localStorage
      const questionsAnswered = memberState.completedAttempts.length;
      
      return {
        hasCompletedToday: false,
        canTakeQuiz: questionsAnswered < QuizSystem.QUESTIONS_PER_DAY,
        questionsAnswered,
        totalQuestions: QuizSystem.QUESTIONS_PER_DAY
      };
    } catch (error) {
      console.error('❌ [QuizSystem] Erro ao obter status do quiz:', error);
      return {
        hasCompletedToday: false,
        canTakeQuiz: true,
        questionsAnswered: 0,
        totalQuestions: QuizSystem.QUESTIONS_PER_DAY
      };
    }
  }
}